import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowLeft, Send, MessageSquare, Search, Check, CheckCheck
} from "lucide-react";
import Layout from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { useMessaging } from "../context/MessagingContext";
import { supabase } from "../lib/supabase";

function ConversationsList({ conversations, activeId, onSelect, getPartnerInfo, userId }) {
  const [search, setSearch] = useState("");

  const filtered = conversations.filter((c) => {
    const partner = getPartnerInfo(c);
    return partner.name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div id="messages_conversations-list" className="flex flex-col h-full">
      <div className="p-4 border-b border-neutral-800/30">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
          <input
            type="text"
            placeholder="Поиск диалогов..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-md bg-neutral-800/50 border border-neutral-700/50 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-600 transition-colors"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <MessageSquare className="w-10 h-10 text-neutral-700 mb-4" />
            <p className="text-lg font-extralight text-neutral-500 text-center">
              {conversations.length === 0 ? "Нет диалогов" : "Ничего не найдено"}
            </p>
          </div>
        ) : (
          filtered.map((conv) => {
            const partner = getPartnerInfo(conv);
            const isActive = activeId === conv.id;
            return (
              <button
                key={conv.id}
                onClick={() => onSelect(conv.id)}
                className={`w-full flex items-center gap-3 p-4 text-left transition-colors border-b border-neutral-800/20 ${
                  isActive
                    ? "bg-primary-500/10"
                    : "hover:bg-neutral-800/30"
                }`}
              >
                {partner.avatar ? (
                  <img
                    src={partner.avatar}
                    alt={partner.name}
                    className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-medium">
                      {partner.name?.[0] || "?"}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className={`text-lg font-medium truncate ${
                      isActive ? "text-primary-400" : "text-white"
                    }`}>
                      {partner.name}
                    </h4>
                    <span className="text-lg font-extralight text-neutral-600 flex-shrink-0 ml-2">
                      {conv.last_message_at
                        ? new Date(conv.last_message_at).toLocaleDateString("ru-RU", {
                            day: "numeric",
                            month: "short"
                          })
                        : ""}
                    </span>
                  </div>
                  <p className="text-lg font-extralight text-neutral-500 truncate">
                    {conv.last_message || "Начните диалог"}
                  </p>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function ChatView({ conversationId, getPartnerInfo, conversations, userId }) {
  const { sendMessage, getMessages, markAsRead } = useMessaging();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const channelRef = useRef(null);

  const conversation = conversations.find((c) => c.id === conversationId);
  const partner = conversation ? getPartnerInfo(conversation) : null;

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    const msgs = await getMessages(conversationId);
    setMessages(msgs);
    await markAsRead(conversationId);
  }, [conversationId, getMessages, markAsRead]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!conversationId) return;

    const channel = supabase
      .channel(`chat-${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          setMessages((prev) => {
            const exists = prev.some((m) => m.id === payload.new.id);
            if (exists) return prev;
            return [...prev, payload.new];
          });
          markAsRead(conversationId);
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [conversationId, markAsRead]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    const sent = await sendMessage(conversationId, newMessage);
    if (sent) {
      setNewMessage("");
    }
    setSending(false);
  };

  if (!conversation || !partner) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
          <p className="text-lg font-extralight text-neutral-500">
            Выберите диалог
          </p>
        </div>
      </div>
    );
  }

  return (
    <div id="messages_chat-view" className="flex flex-col h-full">
      <div className="flex items-center gap-3 p-4 border-b border-neutral-800/30">
        {partner.avatar ? (
          <img
            src={partner.avatar}
            alt={partner.name}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <span className="text-white font-medium">
              {partner.name?.[0] || "?"}
            </span>
          </div>
        )}
        <div>
          <h3 className="text-lg font-medium text-white">{partner.name}</h3>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-lg font-extralight text-neutral-600">
              Напишите первое сообщение
            </p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMine = msg.sender_id === userId;
            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${isMine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-3 rounded-md ${
                    isMine
                      ? "bg-primary-700/80 text-white"
                      : "bg-neutral-800/60 text-neutral-200"
                  }`}
                >
                  <p className="text-lg font-extralight whitespace-pre-wrap break-words">
                    {msg.content}
                  </p>
                  <div className={`flex items-center gap-1 mt-1 ${
                    isMine ? "justify-end" : "justify-start"
                  }`}>
                    <span className={`text-lg font-extralight ${
                      isMine ? "text-primary-300/60" : "text-neutral-600"
                    }`}>
                      {new Date(msg.created_at).toLocaleTimeString("ru-RU", {
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </span>
                    {isMine && (
                      msg.read
                        ? <CheckCheck className="w-3.5 h-3.5 text-primary-300/60" />
                        : <Check className="w-3.5 h-3.5 text-primary-300/40" />
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="p-4 border-t border-neutral-800/30">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Написать сообщение..."
            className="flex-1 px-4 py-3 rounded-md bg-neutral-800/50 border border-neutral-700/50 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-600 transition-colors"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="p-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
}

export default function MessagesPage() {
  const { user } = useAuth();
  const { conversations, getPartnerInfo } = useMessaging();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);

  useEffect(() => {
    const convId = searchParams.get("conv");
    if (convId) {
      setActiveConversationId(convId);
      setMobileShowChat(true);
    }
  }, [searchParams]);

  if (!user) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <MessageSquare className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <h1 className="text-2xl font-medium text-white mb-4">
              Войдите, чтобы читать сообщения
            </h1>
            <button
              onClick={() => navigate("/login")}
              className="px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
            >
              Войти
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const handleSelectConversation = (id) => {
    setActiveConversationId(id);
    setMobileShowChat(true);
  };

  return (
    <Layout hideFooter>
      <div id="messages_page" className="h-[calc(100vh-4rem)] lg:h-[calc(100vh-5rem)] bg-neutral-950 flex">
        <div
          className={`w-full lg:w-96 flex-shrink-0 border-r border-neutral-800/30 bg-neutral-900/30 ${
            mobileShowChat ? "hidden lg:flex lg:flex-col" : "flex flex-col"
          }`}
        >
          <div className="p-4 border-b border-neutral-800/30">
            <h2 className="text-xl font-medium text-white">Сообщения</h2>
          </div>
          <ConversationsList
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={handleSelectConversation}
            getPartnerInfo={getPartnerInfo}
            userId={user.id}
          />
        </div>

        <div
          className={`flex-1 flex flex-col ${
            !mobileShowChat ? "hidden lg:flex" : "flex"
          }`}
        >
          {mobileShowChat && (
            <button
              onClick={() => setMobileShowChat(false)}
              className="lg:hidden flex items-center gap-2 p-3 text-neutral-400 hover:text-white transition-colors border-b border-neutral-800/30"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg font-extralight">Назад</span>
            </button>
          )}

          {activeConversationId ? (
            <ChatView
              conversationId={activeConversationId}
              getPartnerInfo={getPartnerInfo}
              conversations={conversations}
              userId={user.id}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
                <p className="text-xl font-extralight text-neutral-500">
                  Выберите диалог
                </p>
                <p className="text-lg font-extralight text-neutral-600 mt-2">
                  Или напишите эксперту со страницы его профиля
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
